import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { Get, Patch, Post } from "libs/services";
import { USER } from "libs/config";
import { UserResponse } from "src/common/types/response/user";
import { AddressRequest } from "src/common/types/request/updateAddress";
import { openToast } from "./toast";
import { updateAddress } from "./auth";
import { Address } from "src/common/types/auth";
import translate from "libs/locale";
import { Platform } from "react-native";
import { getFileFromUri } from "src/common/libs/utils/image";
import { reportLog } from "src/common/libs/utils/logs";

interface OrdersState {
  data?: UserResponse;
  loading: boolean;
}

export const getUserData = createAsyncThunk(
  "user/getUserData",
  async ({ id }: { id: string }, thunkAPI) => {
    try {
      const response = await Get<UserResponse>(`${USER.user}/${id}`);
      return response.data;
    } catch (error) {
      reportLog({
        error: error,
        location: "features/user->getUserData",
        body: { id },
      });
      return null;
    }
  }
);

export const updateUserImage = createAsyncThunk(
  "user/updateUserImage",
  async ({ id, file }: { id: string; file: string }, thunkAPI) => {
    try {
      const formData = new FormData();
      const imageData = await getFileFromUri(file);
      formData.append("file", imageData);
      formData.append("_id", id);

      const response = await Patch<UserResponse>(USER.image, formData, null, {
        "content-type": "multipart/form-data",
      });
      thunkAPI.dispatch(getUserData({ id }));
      return response.data;
    } catch (error) {
      reportLog({
        error: error,
        location: "features/user->updateUserImage",
        body: { id, file },
      });
      thunkAPI.dispatch(
        openToast({
          message: translate("failedUploadImage") || error.message,
          type: "fail",
        })
      );
      return null;
    }
  }
);

export const updateUserData = createAsyncThunk(
  "user/patchUserData",
  async (
    {
      data,
      type,
      preText = "address",
      headers = {},
      showMessage = true,
    }: {
      data: any;
      type?: string;
      preText?: string;
      headers?: {};
      showMessage?: boolean;
    },
    thunkAPI
  ) => {
    try {
      const response = await Patch<UserResponse>(
        USER.user,
        data,
        null,
        headers
      );
      let message;
      switch (type) {
        case "edit_true":
          message = "Removed";
          break;
        case "edit_false":
          message = "Updated";
          break;
        case "profile":
          message = "Updated";
          preText = "profile";
          break;
        default:
          message = "Added";
          break;
      }
      if (showMessage) {
        thunkAPI.dispatch(
          openToast({
            message: translate(`${preText}${message}`),
            type: "success",
          })
        );
      }
      thunkAPI.dispatch(
        updateAddress(response.data?.payload?.address as unknown as Address)
      );
      return response.data;
    } catch (error) {
      reportLog({
        error: error,
        location: "features/user->updateUserData",
        body: { data, type, preText, headers, showMessage },
      });
      thunkAPI.dispatch(
        openToast({ message: error.response?.data?.message || error.message })
      );
      return null;
    }
  }
);

const initialState: OrdersState = {
  data: null,
  loading: false,
};

export const userSlice = createSlice({
  name: "user",
  initialState,
  reducers: {
    clearData: () => initialState,
  },
  extraReducers: (builder) => {
    builder.addCase(getUserData.pending, (state, action) => {
      state.loading = true;
    });
    builder.addCase(getUserData.fulfilled, (state, action) => {
      state.data = action.payload;
      state.loading = false;
    });
    builder.addCase(getUserData.rejected, (state, action) => {
      state.data = null;
      state.loading = false;
    });

    builder.addCase(updateUserData.fulfilled, (state, action) => {
      state.data = action.payload;
    });
  },
});

export const { clearData } = userSlice.actions;

export default userSlice.reducer;
